import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { Aviso, AreaTexto, Botao, Campo, Entrada, Modal, Selecao } from '../../componentes/ui.jsx';
import './pacientes.css';

/**
 * Cadastro de paciente (administrativo, sem prontuario), compartilhado pela ficha
 * do contato e pelo assistente de agendamento. Um lugar so, para os dois falarem
 * a mesma lingua com a API.
 */

export const PARENTESCOS = {
  proprio: 'É o próprio contato',
  filho: 'Filho(a)',
  pai_mae: 'Pai ou mãe',
  conjuge: 'Cônjuge',
  outro: 'Outro'
};

export const SEXOS = { feminino: 'Feminino', masculino: 'Masculino', outro: 'Outro' };

/** Idade em anos inteiros (ou null). Calculada na hora: nunca fica velha. */
export function idadeAnos(nascimento) {
  if (!nascimento) return null;
  const [a, m, d] = nascimento.split('-').map(Number);
  const hoje = new Date();
  let anos = hoje.getFullYear() - a;
  if (hoje.getMonth() + 1 < m || (hoje.getMonth() + 1 === m && hoje.getDate() < d)) anos -= 1;
  return anos >= 0 ? anos : null;
}

/** "34 anos", "8 meses" ou null (sem nascimento). */
export function idadeTexto(nascimento) {
  if (!nascimento) return null;
  const [a, m, d] = nascimento.split('-').map(Number);
  const hoje = new Date();
  let meses = (hoje.getFullYear() - a) * 12 + (hoje.getMonth() + 1 - m);
  if (hoje.getDate() < d) meses -= 1;
  if (meses < 0) return null;
  if (meses < 1) return 'recém-nascido';
  if (meses < 12) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  const anos = Math.floor(meses / 12);
  return `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
}

/** Idade em anos do paciente: prefere a que a API calcula; cai no nascimento. */
const idadeDe = (p) => (typeof p.idade === 'number' ? p.idade : idadeAnos(p.nascimento));

export const textoAnos = (n) => (n === null || n === undefined ? null : `${n} ${n === 1 ? 'ano' : 'anos'}`);

/** "Particular" ou o nome do convenio. */
export const convenioTexto = (p) => p?.convenio || 'Particular';

/** "34 anos · Filho(a) · Unimed": o que cabe numa linha sobre o paciente. */
export function resumoDoPaciente(p) {
  return [
    textoAnos(idadeDe(p)),
    p.parentesco && p.parentesco !== 'proprio' ? PARENTESCOS[p.parentesco] : null,
    convenioTexto(p)
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Mascara 000.000.000-00 enquanto digita. */
export function mascararCpf(valor) {
  const d = String(valor ?? '').replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

/** Nome do paciente de uma OS, ou nada (OS antiga, sem paciente, nao mostra "undefined"). */
export function PacienteDaOS({ a, className = 'paciente-tag texto-fraco' }) {
  if (!a?.pacienteNome) return null;
  return <span className={className}>{a.pacienteNome}</span>;
}

/** Lista compacta dos pacientes do contato (painel da conversa, popup de perfil). */
export function PacientesDoContato({ pacientes = [] }) {
  if (pacientes.length === 0) return <p className="texto-fraco">Nenhum paciente cadastrado.</p>;
  return (
    <ul className="pacientes-mini">
      {pacientes.map((p) => (
        <li key={p.id}>
          <strong>{p.nomeSocial || p.nome}</strong>
          <span className="texto-fraco">{resumoDoPaciente(p)}</span>
        </li>
      ))}
    </ul>
  );
}

const formVazio = {
  nome: '',
  nomeSocial: '',
  nascimento: '',
  cpf: '',
  sexo: '',
  parentesco: 'outro',
  convenio: '',
  carteirinha: '',
  responsavelNome: '',
  observacoes: ''
};

function formDoPaciente(p) {
  return {
    nome: p.nome,
    nomeSocial: p.nomeSocial ?? '',
    nascimento: p.nascimento ?? '',
    cpf: '', // na edicao o CPF fica oculto; so vai para a API se digitarem um novo
    sexo: p.sexo ?? '',
    parentesco: p.parentesco ?? 'proprio',
    convenio: p.convenio ?? '',
    carteirinha: p.carteirinha ?? '',
    responsavelNome: p.responsavelNome ?? '',
    observacoes: p.observacoes ?? ''
  };
}

/**
 * @param {object} p
 * @param {string} p.leadId
 * @param {string} [p.nomeContato]   usado no atalho "o paciente e o proprio contato"
 * @param {object} [p.paciente]      quando vem, edita; senao cadastra
 * @param {boolean} [p.primeiro]     true quando o contato ainda nao tem paciente (mostra o atalho)
 * @param {(paciente:object)=>void} [p.aoSalvar]
 */
export function ModalPaciente({ leadId, nomeContato = '', paciente = null, primeiro = false, aoFechar, aoSalvar }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(paciente ? formDoPaciente(paciente) : formVazio);
  const [erroLocal, setErroLocal] = useState(null);
  const muda = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const salvar = useMutation({
    mutationFn: (corpo) =>
      paciente
        ? api.patch(`/api/pacientes/${paciente.id}`, corpo)
        : api.post(`/api/leads/${leadId}/pacientes`, corpo),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['pacientes', leadId] });
      aoSalvar?.(r.paciente);
    }
  });

  const idade = idadeAnos(form.nascimento);
  const exigeResponsavel = idade !== null && idade < 18 && form.parentesco !== 'proprio';

  function enviar() {
    setErroLocal(null);
    if (!form.nome.trim()) return setErroLocal('Informe o nome do paciente.');
    if (exigeResponsavel && !form.responsavelNome.trim()) {
      return setErroLocal('Paciente menor de 18 anos: informe o nome do responsável.');
    }
    const cpfDigitos = form.cpf.replace(/\D/g, '');
    if (cpfDigitos && cpfDigitos.length !== 11) return setErroLocal('CPF incompleto. Use 11 dígitos.');

    // Na edicao, campo limpo vira null (apaga); no cadastro, simplesmente nao vai.
    const vazio = paciente ? null : undefined;
    const corpo = {
      nome: form.nome.trim(),
      nomeSocial: form.nomeSocial.trim() || vazio,
      nascimento: form.nascimento || vazio,
      sexo: form.sexo || vazio,
      parentesco: form.parentesco,
      convenio: form.convenio.trim() || vazio,
      carteirinha: form.carteirinha.trim() || vazio,
      responsavelNome: form.responsavelNome.trim() || vazio,
      observacoes: form.observacoes.trim()
    };
    // CPF so viaja se o usuario digitou (na edicao o servidor guarda o antigo).
    if (cpfDigitos) corpo.cpf = form.cpf;
    salvar.mutate(corpo);
  }

  function ehOProprio() {
    setForm({ ...form, nome: nomeContato || form.nome, parentesco: 'proprio' });
  }

  const erros = salvar.error?.camposComErro ?? {};

  return (
    <Modal
      titulo={paciente ? `Editar ${paciente.nome}` : 'Novo paciente'}
      aberto
      aoFechar={aoFechar}
      largura={600}
      rodape={
        <>
          <Botao variante="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao carregando={salvar.isPending} onClick={enviar}>
            {paciente ? 'Salvar' : 'Cadastrar paciente'}
          </Botao>
        </>
      }
    >
      <div className="form-paciente">
        {(erroLocal || salvar.isError) && <Aviso tom="perigo">{erroLocal ?? salvar.error.message}</Aviso>}

        {!paciente && primeiro && nomeContato && (
          <Botao variante="secundario" tamanho="sm" onClick={ehOProprio}>
            O paciente é o próprio contato
          </Botao>
        )}

        <div className="form-paciente__duas">
          <Campo rotulo="Nome completo" obrigatorio erro={erros.nome}>
            <Entrada autoFocus value={form.nome} maxLength={120} onChange={muda('nome')} placeholder="Maria da Silva" />
          </Campo>
          <Campo rotulo="Nome social" erro={erros.nomeSocial} dica="Se houver, é o nome usado no atendimento.">
            <Entrada value={form.nomeSocial} maxLength={120} onChange={muda('nomeSocial')} />
          </Campo>
        </div>

        <div className="form-paciente__duas">
          <Campo rotulo="Data de nascimento" erro={erros.nascimento} dica={textoAnos(idade) ?? undefined}>
            <Entrada
              type="date"
              value={form.nascimento}
              max={new Date().toLocaleDateString('sv-SE')}
              onChange={muda('nascimento')}
            />
          </Campo>
          <Campo rotulo="Sexo">
            <Selecao value={form.sexo} onChange={muda('sexo')}>
              <option value="">Não informado</option>
              {Object.entries(SEXOS).map(([chave, rotulo]) => (
                <option key={chave} value={chave}>
                  {rotulo}
                </option>
              ))}
            </Selecao>
          </Campo>
        </div>

        <div className="form-paciente__duas">
          <Campo
            rotulo="CPF"
            erro={erros.cpf}
            dica={paciente?.cpfMascarado ? `Cadastrado: ${paciente.cpfMascarado}. Digite só para trocar.` : undefined}
          >
            <Entrada
              value={form.cpf}
              inputMode="numeric"
              maxLength={14}
              onChange={(e) => setForm({ ...form, cpf: mascararCpf(e.target.value) })}
              placeholder={paciente?.cpfMascarado ?? '000.000.000-00'}
            />
          </Campo>
          <Campo rotulo="Parentesco com o contato">
            <Selecao value={form.parentesco} onChange={muda('parentesco')}>
              {Object.entries(PARENTESCOS).map(([chave, rotulo]) => (
                <option key={chave} value={chave}>
                  {rotulo}
                </option>
              ))}
            </Selecao>
          </Campo>
        </div>

        <div className="form-paciente__duas">
          <Campo rotulo="Convênio" dica="Deixe vazio para Particular.">
            <Entrada value={form.convenio} maxLength={80} onChange={muda('convenio')} placeholder="Particular" />
          </Campo>
          <Campo rotulo="Carteirinha" erro={erros.carteirinha}>
            <Entrada value={form.carteirinha} maxLength={40} onChange={muda('carteirinha')} />
          </Campo>
        </div>

        {(exigeResponsavel || form.responsavelNome) && (
          <Campo
            rotulo="Responsável"
            obrigatorio={exigeResponsavel}
            erro={erros.responsavelNome}
            dica={exigeResponsavel ? 'Obrigatório para menores de 18 anos.' : undefined}
          >
            <Entrada value={form.responsavelNome} maxLength={120} onChange={muda('responsavelNome')} />
          </Campo>
        )}

        <Campo rotulo="Observações administrativas" dica="Acessibilidade, acompanhante, preferência de horário.">
          <AreaTexto value={form.observacoes} maxLength={2000} onChange={muda('observacoes')} />
        </Campo>
        <Aviso tom="info">Não registre dados clínicos aqui (sintomas, diagnósticos, remédios).</Aviso>
      </div>
    </Modal>
  );
}
